// eslint-disable-next-line @alltrails/custom/client-navigation
import { useSearchParams } from 'next/navigation';
import { ReactNode, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import useToggle from '@alltrails/core/hooks/useToggle';
import Coupon from '@alltrails/core/types/Coupon';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import { ApiError } from '@alltrails/core/utils/api';
import Button from '@alltrails/denali/components/Button';
import Link from '@alltrails/denali/components/Link';
import TextInput from '@alltrails/denali/components/TextInput';
import CheckCircleFilled from '@alltrails/denali/icons/CheckCircleFilled';
import verifyCoupon from '../../utils/verifyCoupon';
import styles from './CouponCode.module.scss';

type Props = {
  coupon?: Coupon;
  couponError?: string;
  purchaseTier: PurchaseTier;
  setCoupon: (coupon: Coupon) => void;
  setPlanToAnnual: () => void;
};

// Desktop: https://www.figma.com/file/vJ30CAAm8qdbDO0j5C4Ce7/Web-Pro-Purchase-Flow
export default function CouponCode({ couponError, coupon, purchaseTier, setCoupon, setPlanToAnnual }: Props) {
  const [error, setError] = useState<ReactNode>();
  const [isOpen, toggle] = useToggle(Boolean(coupon));
  const [value, setValue] = useState(coupon?.code || '');
  const params = useSearchParams();
  const intl = useIntl();

  // Display a coupon error on page load if no coupon data exists for an existing coupon query param.
  useEffect(() => {
    const couponParam = params.get('k');
    if (couponParam && !coupon) {
      setValue(couponParam);
      if (couponError) {
        setError(couponError);
      } else {
        setError(<FormattedMessage defaultMessage="This coupon code is invalid" />);
      }
      toggle();
    }
  }, [coupon, couponError, params, toggle]);

  async function handleClick() {
    if (value) {
      try {
        const coupons = await verifyCoupon(value, purchaseTier);

        // clear a previous bad coupon
        setError(undefined);

        setCoupon(coupons[0]);

        const params = new URLSearchParams(window.location.search);
        params.set('k', coupons[0].code);
        window.history.replaceState({}, '', `${window.location.pathname}?${params}`);

        setPlanToAnnual();
      } catch (e) {
        if (e instanceof ApiError && e.data?.errors?.length > 0) {
          setError(e.data.errors[0].message);
        } else {
          setError(<FormattedMessage defaultMessage="This coupon code is invalid" />);
        }
      }
    }
  }

  return isOpen ? (
    <div className={styles.content}>
      <div className={styles.coupon}>
        <TextInput
          className={styles.input}
          onChange={(value: string) => {
            if (error) {
              setError(undefined);
            }
            setValue(value);
          }}
          errorText={error}
          disabled={Boolean(coupon)}
          trailingIcon={coupon ? { Component: CheckCircleFilled } : undefined}
          helperText={coupon ? <FormattedMessage defaultMessage="Coupon applied" /> : undefined}
          placeholder={intl.formatMessage({ defaultMessage: 'Coupon code' })}
          value={value}
        />
        <Button
          onClick={handleClick}
          text={coupon ? <FormattedMessage defaultMessage="Applied" /> : <FormattedMessage defaultMessage="Apply" />}
          variant="secondary"
        />
      </div>
      <Link className={styles.button} size="sm" onClick={toggle}>
        <FormattedMessage defaultMessage="Hide coupon code" />
      </Link>
    </div>
  ) : (
    <Link className={styles.button} size="sm" onClick={toggle}>
      <FormattedMessage defaultMessage="Enter a coupon code" />
    </Link>
  );
}
